import type { Clarification, DraftOutcome, Eligibility, RefusedStep } from "../api/types.js";
import { answerableAll } from "./clarify.js";
import { replyOf } from "./surfaces.js";

/**
 * DCTA-176 F44 (docs/design/v3/04-kyc.md §0.2, §2.4) — what an outcome with nothing to sign DRAWS, decided once. The chat
 * (`Chat.#otherOutcome`) draws exactly this, and the read-aloud (`read-aloud.ts › outcomeParts`) names exactly these
 * objects, in this order — so what is heard is what is on the screen.
 *
 *  - One bank message per refusal: refusals with the same rule, the same words and the same way on collapse into ONE,
 *    naming every step (B57's rule, mirrored as defence in depth). A refusal that holds an offer of its own (answered by
 *    id, scoped to its step) never collapses.
 *  - Eligibility before clarification (PQ-06): under the identity check's turn refusal (`scope: "turn"`, B55) nothing
 *    about a payee, an amount or a share is asked — not even when an answer arrives carrying a question.
 *  - A protection or a read that ran beside a refused payment: its result line, then the refusal (§2.1 — the read is
 *    answered, the payment gets row 24a once).
 */
export interface OutcomePlan {
  /** Small talk's one fixed sentence (DCTA-127) — nothing else is drawn. */
  readonly reply?: string;
  /** The bank's result lines for what ran without a signature (a tightening). */
  readonly executed: readonly string[];
  /** The refusals as drawn, in the bank's order — identical ones collapsed. */
  readonly refusals: readonly RefusedStep[];
  /** The question drawn after the refusals (the first one); never under a turn refusal. */
  readonly question: Clarification | undefined;
  /**
   * DCTA-189 (QA DW-4): every question the card draws, in the bank's order — the first, and each other question of the
   * turn that is answerable by its own id ("Two Johns are saved. Which one?" · "And fifty — …"); none under a turn refusal.
   */
  readonly questions: readonly Clarification[];
}

/** An action that answers ONE step by id — an offer: such a refusal never collapses into another step's. */
const offers = (r: RefusedStep): boolean => (r.next_actions ?? []).some((a) => a.action.kind === "answer");

/** Everything the customer would see or tap, in one key: the same key is the same message. */
function keyOf(r: RefusedStep): string {
  return JSON.stringify([
    r.reason_code,
    r.rule_ids,
    r.customer_message,
    r.next_steps ?? [],
    (r.next_actions ?? []).map((a) => [a.label, a.action]),
    r.available_at ?? "",
    r.scope ?? "",
  ]);
}

/** F44 (B57 mirrored): identical refusals inside one outcome → ONE, with the union of the steps they cover. */
export function collapseRefusals(refused: readonly RefusedStep[]): RefusedStep[] {
  const out: RefusedStep[] = [];
  const at = new Map<string, number>();
  for (const r of refused) {
    const key = offers(r) ? undefined : keyOf(r);
    const i = key === undefined ? undefined : at.get(key);
    if (key === undefined || i === undefined) {
      if (key !== undefined) at.set(key, out.length);
      out.push(r);
      continue;
    }
    const first = out[i] as RefusedStep;
    out[i] = { ...first, step_ids: [...new Set([...(first.step_ids ?? [first.step_id]), ...(r.step_ids ?? [r.step_id])])] };
  }
  return out;
}

/** B55: the identity check refused the whole turn — nothing may be asked under it. */
export function turnRefused(outcome: Pick<DraftOutcome, "refused_steps">): boolean {
  return (outcome.refused_steps ?? []).some((r) => r.scope === "turn");
}

export function outcomePlan(outcome: DraftOutcome): OutcomePlan {
  const reply = replyOf(outcome);
  if (reply !== undefined) return { reply, executed: [], refusals: [], question: undefined, questions: [] };
  const executed = (outcome.executed ?? []).map((e) => e.result_summary).filter((t): t is string => t !== undefined && t !== "");
  const refusals = collapseRefusals(outcome.refused_steps ?? []);
  // MUTATION TARGET: without it the bank's question about "tos" is drawn under the lapsed-check refusal (the 12 Oct bug).
  const question = turnRefused(outcome) ? undefined : outcome.clarifications?.[0];
  const together = question === undefined ? [] : answerableAll(outcome);
  return { executed, refusals, question, questions: question === undefined ? [] : together.length > 0 ? together : [question] };
}

/**
 * F49 (§2.5): judge mode's line under the turn refusal — the bank's own stage, never more than it said:
 * "POL-KYC-GATE · kyc restricted · checked before any question".
 */
export function eligibilityLine(e: Eligibility): string {
  const before = e.before === "resolution" && e.question_asked === false ? " · checked before any question" : "";
  return `${e.rule_id} · kyc ${e.kyc_status}${before}`;
}
